import classNames from 'classnames';
import React, { useRef } from 'react';
import { Popover } from '@meitu/saas-ui';
import { isPad } from '@meitu/saas-editor-core/es/utils/browser-kit';
import { type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { EditorContext, useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { useLocaleStore } from '@/i18n';
import { useEffectiveShapeExtension } from '../../hooks';
import { ShapeTextPanel } from '../../panels';
import { type ShapeTextMaterialRaw, type WidgetCommonProps } from '../../types';
import './style.less';

type Props = React.ComponentProps<'div'> & {
    shapeTextList: ShapeTextMaterialRaw[];
} & WidgetCommonProps;

export const ShapeTextEntry: React.FC<Props> = ({ shapeTextList, style, theme, ...restProps }) => {
    const { $t } = useLocaleStore();
    const editor = useEditor();

    const popoverRef = useRef<any>(null);
    const popoverWidgetRef = useGlobalWidget(editor, false);

    const props = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        return { advancedTextMaterial: layerData.advancedTextMaterial };
    });

    const shapeExtension = useEffectiveShapeExtension(restProps.layerId);
    const advancedTextMaterial = props ? props.advancedTextMaterial : null;

    const thumbnail = (() => {
        if (!shapeExtension) return null;

        const material = shapeTextList.find((item) => item['shape_type'] === shapeExtension.curveTextType);
        if (!material) return null;

        const lightThumbnail = material.thumbnail;
        const darkThumbnail = material['thumbnail_black'];

        if (theme === 'dark') return darkThumbnail || lightThumbnail;
        return lightThumbnail;
    })();

    const disabled = !!advancedTextMaterial;

    const rootCls = classNames('modal-panel-entry', { 'modal-panel-entry--disabled': disabled || isPad });

    const handleClick = async (e: React.MouseEvent<HTMLDivElement>) => {
        if (disabled || isPad) return e.stopPropagation();
    };

    const handleOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;
    };

    return (
        <Popover
            trigger="click"
            placement="left"
            ref={popoverRef}
            content={
                <EditorContext.Provider value={editor}>
                    <ShapeTextPanel
                        {...restProps}
                        theme={theme}
                        shapeTextList={shapeTextList}
                        onClose={() => {
                            // 模拟弹窗外点击关闭弹窗
                            popoverRef.current?.onClick?.();
                        }}
                    />
                </EditorContext.Provider>
            }
            overlayClassName="text-layer-panel-popover"
            destroyTooltipOnHide
            onOpenChange={handleOpenChange}
        >
            <div className={rootCls} onClickCapture={handleClick} style={style}>
                <div className="modal-panel-entry__thumbnail modal-panel-entry__thumbnail--flex">
                    {thumbnail ? (
                        <img className="modal-panel-entry__img" src={thumbnail.url} />
                    ) : (
                        <IconFont name="SlashCircle" fontSize={28} />
                    )}
                </div>
                <div className="modal-panel-entry__title">{$t((v) => v.panel.textPanel.transform)}</div>
            </div>
        </Popover>
    );
};
